import createAuth0Client, {
  Auth0Client,
  Auth0ClientOptions,
  GetTokenSilentlyOptions,
  LogoutOptions,
  RedirectLoginOptions,
} from '@auth0/auth0-spa-js'

interface Auth0Instance {
  isInitialized: boolean
  loginWithPopup: () => Promise<boolean>
  loginWithRedirect: (
    options?: RedirectLoginOptions
  ) => ReturnType<Auth0Client['loginWithRedirect']>
  handleRedirectCallback: () => ReturnType<
    Auth0Client['handleRedirectCallback']
  >
  logout: (options?: LogoutOptions) => ReturnType<Auth0Client['logout']>
  isAuthenticated: () => ReturnType<Auth0Client['isAuthenticated']>
  getToken: (
    options?: GetTokenSilentlyOptions,
    usePopup?: boolean,
    throwException?: boolean
  ) => Promise<string | null>
}

let instance: Auth0Instance | null = null

const createInstance = async (
  options: Auth0ClientOptions
): Promise<Auth0Instance> => {
  let isInitialized = false
  let instance: Auth0Client
  try {
    instance = await createAuth0Client(options)
    isInitialized = true
  } catch {
    console.error('Auth0: Initialization error')
  }

  return {
    isInitialized,
    async loginWithPopup() {
      try {
        await instance.loginWithPopup()
        return instance.isAuthenticated()
      } catch {
        return false
      }
    },
    async loginWithRedirect(options?: RedirectLoginOptions) {
      return instance.loginWithRedirect(options)
    },
    async handleRedirectCallback() {
      return instance.handleRedirectCallback()
    },
    async logout(logoutOptions?: LogoutOptions) {
      return instance.logout(logoutOptions)
    },
    async isAuthenticated() {
      return instance.isAuthenticated()
    },
    async getToken(
      options?: GetTokenSilentlyOptions,
      usePopup = false,
      throwException = false
    ) {
      // In dev and staging environments, we have localhost configured as part of redirect urls, Auth0
      // does not trust this hence we cannot use the getTokenSilently to fetch access tokens for
      // audiences different from the one specified for initial authentication. The solution is to
      // show consent popups to user in test dev/local environments. If the `usePopup` is set to
      // `true`, then whenever getToken is called, an initial consent screen is shown to the user.
      // This consent screen is not be desirable in production environments but only a way around in
      // dev/local environments.
      try {
        if (usePopup) {
          return (await instance.getTokenWithPopup(options)) ?? null
        }
        return await instance.getTokenSilently(options)
      } catch (e) {
        if (throwException) {
          throw e
        }
        return null
      }
    },
  }
}

export const initAuth0 = async (options: Auth0ClientOptions) => {
  instance = await createInstance(options)
}

export const getAuth0Instance = () => instance as Auth0Instance

export type { GetTokenSilentlyOptions, Auth0Instance, LogoutOptions }
